/* eslint-disable no-console */
import React from 'react';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useNavigate, useMatch, useLocation } from 'react-router-dom';
import { Alert, Badge, Button, Card, ListGroup, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import { client } from '../../lib/AmplifyClient';
import type { Schema } from '../../../amplify/data/resource';
import { useUserContext } from '../Context/UserContext';

type Issue = Schema['Issue']['type'];

const GOOGLE_GREEN = '#34A853';
const GOOGLE_BLUE = '#4285F4';
const GOOGLE_RED = '#EA4335';

/** ---------- helpers ---------- */
function unwrap<T>(maybeWrapped: unknown): T | null {
  if (maybeWrapped && typeof maybeWrapped === 'object' && 'data' in (maybeWrapped as any)) {
    const { data } = (maybeWrapped as any) as { data: T | null };
    return data ?? null;
  }
  return (maybeWrapped as T) ?? null;
}

function coerceStrings(arr?: (string | null)[] | null): string[] {
  return (arr ?? []).filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
}

function readCandidates(issue: Issue | null): Array<{ statement: string; conflictType?: string; score?: number }> {
  if (!issue) return [];
  const pj = (issue as any).proposedIssueCandidatesJson as
    | { candidates?: Array<{ statement?: string; conflictType?: string; score?: number }> }
    | string
    | null
    | undefined;

  let structured: Array<{ statement?: string; conflictType?: string; score?: number }> = [];
  if (pj) {
    try {
      const parsed = typeof pj === 'string' ? JSON.parse(pj) : pj;
      if (parsed && Array.isArray((parsed as any).candidates)) {
        structured = (parsed as any).candidates;
      }
    } catch {}
  }

  const mirrored = coerceStrings((issue as any).reframedIssueCandidates);

  let merged: Array<{ statement: string; conflictType?: string; score?: number }> = [];
  if (structured.length > 0) {
    merged = structured
      .map((c) => ({
        statement: String(c?.statement ?? '').trim(),
        conflictType: c?.conflictType,
        score: typeof c?.score === 'number' ? c?.score : undefined,
      }))
      .filter((c) => c.statement.length > 0);
  } else {
    merged = mirrored.map((s) => ({ statement: s }));
  }

  merged.sort((a, b) => (b.score ?? -Infinity) - (a.score ?? -Infinity));
  return merged.slice(0, 4);
}

function fullName(first?: string | null, last?: string | null) {
  const n = `${first ?? ''} ${last ?? ''}`.trim();
  return n || '—';
}

function humanizeStatus(s?: string) {
  const t = (s ?? 'created').replace(/_/g, ' ');
  return t.replace(/\b\w/g, (c) => c.toUpperCase());
}

function statusVariant(s?: string): string {
  switch (s) {
    case 'questions':
      return 'primary';
    case 'accepted':
      return 'primary';
    case 'resolving_issue':
      return 'info';
    case 'agreed_on_issue':
      return 'success';
    case 'resolved':
      return 'success';
    case 'prepare':
      return 'warning';
    case 'propose':
    case 'strategies':
      return 'warning';
    case 'implemented':
      return 'success';
    case 'closed':
      return 'dark';
    case 'created':
    default:
      return 'secondary';
  }
}

function safeDateTime(v: any): string {
  if (!v) return '';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString();
  } catch {
    return '';
  }
}

function norm(s?: string | null) {
  return (s ?? '').trim().toLowerCase();
}

function possessive(name?: string | null): string | null {
  const n = (name ?? '').trim();
  if (!n) return null;
  return /s$/i.test(n) ? `${n}'` : `${n}'s`;
}

/** Map Issue.status → route */
function routeForStatus(id: string, status?: string | null): string | null {
  const s = (status ?? '').toLowerCase();
  if (!id) return null;
  switch (s) {
    case 'questions':
      return `/session/${encodeURIComponent(id)}/questions`;
    case 'accepted':
      return `/session/${encodeURIComponent(id)}/questions`;
    case 'agreed_on_issue':
      return `/session/${encodeURIComponent(id)}/solutions`;
    case 'resolving_issue':
      return `/session/${encodeURIComponent(id)}/solutions`;
    case 'resolved':
      return `/session/${encodeURIComponent(id)}/resolve`;
    case 'prepare':
      return `/session/${encodeURIComponent(id)}/prepare`;
    case 'propose':
    case 'strategies':
      return `/session/${encodeURIComponent(id)}/propose-strategies`;
    case 'implemented':
      return `/session/${encodeURIComponent(id)}/implement`;
    default:
      return null;
  }
}

/** A shared full-height flex wrapper to center content on screen */
const shellStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem 1rem',
};

/** ---------- component ---------- */
export default function SessionPageComponent() {
  const matchDetail = useMatch('/session/:id/*');
  const sessionId = useMemo(
    () => decodeURIComponent(matchDetail?.params.id ?? ''),
    [matchDetail?.params.id]
  );
  const isListView = !matchDetail;

  const navigate = useNavigate();
  const location = useLocation();
  const { email: userEmail, firstName } = useUserContext();

  const [loading, setLoading] = useState(true);
  const [issue, setIssue] = useState<Issue | null>(null);
  const [sessions, setSessions] = useState<Issue[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [archivingIds, setArchivingIds] = useState<Set<string>>(new Set());
  const [archivingDetail, setArchivingDetail] = useState<boolean>(false);
  const mounted = useRef(true);

  const [showInviteToast, setShowInviteToast] = useState(false);

  useEffect(() => {
    if (!matchDetail) return;
    const stateFlag = (location.state as any)?.showInviteToast === true;
    const qp = new URLSearchParams(location.search);
    const queryFlag = qp.get('invited') === '1';

    if (stateFlag || queryFlag) {
      setShowInviteToast(true);

      if (stateFlag) {
        navigate(location.pathname, { replace: true, state: {} });
      } else if (queryFlag) {
        qp.delete('invited');
        const clean = `${location.pathname}${qp.toString() ? `?${qp.toString()}` : ''}`;
        navigate(clean, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchDetail, location.key]);

  async function readIssue(targetId: string) {
    try {
      const raw = await client.models.Issue.get(
        { id: targetId },
        { authMode: 'userPool' }
      );
      const data = unwrap<Issue>(raw);
      if (mounted.current) setIssue(data);
      if (!data) setError('Session not found or you do not have access.');
      return data;
    } catch {
      setError('Failed to fetch issue.');
      return null;
    }
  }

  async function readMySessions() {
    const me = (userEmail ?? '').toLowerCase();
    if (!me) {
      setSessions([]);
      return [];
    }

    try {
      const [byI, byC] = await Promise.all([
        (client as any).models.Issue.listIssuesByInitiatorEmail(
          { initiatorEmail: me, sortDirection: 'DESC', limit: 200 },
          { authMode: 'userPool' }
        ),
        (client as any).models.Issue.listIssuesByCollaboratorEmail(
          { collaboratorEmail: me, sortDirection: 'DESC', limit: 200 },
          { authMode: 'userPool' }
        ),
      ]);

      const a = (byI?.data ?? []) as Issue[];
      const b = (byC?.data ?? []) as Issue[];

      const seen = new Set<string>();
      const merged: Issue[] = [];
      for (const s of [...a, ...b]) {
        if (!s?.id || seen.has(s.id)) continue;
        seen.add(s.id);
        merged.push(s);
      }

      const filtered = merged.filter((s) => (s as any).status !== 'closed');

      filtered.sort((x, y) => {
        const tx = Date.parse((x as any).lastActivityAt ?? (x as any).createdAt ?? '') || 0;
        const ty = Date.parse((y as any).lastActivityAt ?? (y as any).createdAt ?? '') || 0;
        return ty - tx;
      });

      if (mounted.current) setSessions(filtered);
      return filtered;
    } catch {
      setError('Failed to fetch sessions.');
      return sessions ?? [];
    }
  }

  const archiveById = useCallback(async (id: string) => {
    const now = new Date().toISOString();

    const getRes = await (client as any).models.Issue.get({ id }, { authMode: 'userPool' }).catch(() => null);
    const current = (getRes?.data ?? getRes) as any | null;
    const _version = current?._version;

    const patch = {
      id,
      status: 'closed',
      closedAt: now,
      lastActivityAt: now,
      ...(_version != null ? { _version } : {}),
    } as any;

    try {
      const res = await (client as any).models.Issue.update(patch, { authMode: 'userPool' });
      const entity = (res?.data ?? res) as any;
      if (entity?.status === 'closed') return;
    } catch {
      // fall through to GraphQL
    }

    const mutation = /* GraphQL */ `
      mutation UpdateIssue($input: UpdateIssueInput!) {
        updateIssue(input: $input) { id status closedAt lastActivityAt }
      }
    `;
    const g = await (client as any).graphql({
      query: mutation,
      variables: { input: patch },
      authMode: 'userPool',
    });

    if (!g?.data?.updateIssue) {
      const err = (g as any)?.errors?.[0]?.message || 'Unknown error';
      throw new Error(err);
    }
  }, []);

  const archiveSession = useCallback(
    async (id: string) => {
      const ok = window.confirm('Delete this session? Both participants will no longer see it.');
      if (!ok) return;

      setArchivingIds((prev) => new Set(prev).add(id));
      const prevList = sessions ?? [];
      setSessions(prevList.filter((s) => s.id !== id));

      try {
        await archiveById(id);
        await readMySessions();
      } catch {
        setSessions(prevList);
        setError('Failed to delete session. Please try again.');
      } finally {
        setArchivingIds((prev) => {
          const n = new Set(prev);
          n.delete(id);
          return n;
        });
      }
    },
    [sessions, archiveById]
  );

  const archiveCurrentSession = useCallback(
    async () => {
      if (!issue?.id) return;
      const ok = window.confirm('Delete this session? The session will not be available.');
      if (!ok) return;

      setArchivingDetail(true);
      try {
        await archiveById(issue.id);
        navigate('/session', { replace: true });
      } catch {
        setError('Failed to delete session. Please try again.');
        navigate('/session', { replace: true });
      } finally {
        setArchivingDetail(false);
      }
    },
    [issue?.id, archiveById, navigate]
  );

  // Initial fetch
  useEffect(() => {
    mounted.current = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        if (isListView) {
          await readMySessions();
        } else if (sessionId) {
          await readIssue(sessionId);
        }
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();

    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListView, sessionId, userEmail]);

  // Live subscription
  useEffect(() => {
    if (!sessionId) return;

    readIssue(sessionId).catch(() => {});

    const selection = `
      id
      status
      collaboratorJoined
      initiatorEmail
      initiatorFirstName
      initiatorLastName
      collaboratorEmail
      collaboratorFirstName
      collaboratorLastName
      initiatorIssue
      desiredOutcomeInitiator
      collaboratorIssue
      desiredOutcomeCollaborator
      whyOutcomeMattersCollaborator
      proposedIssueCandidatesJson
      reframedIssueCandidates
      agreedIssue
      bestSolution
      inferredQuestionsInitiator
      initiatorQuestionAnswers
      collaboratorQuestionAnswers
      createdAt
      lastActivityAt
    `;

    const sub = (client as any)
      .graphql({
        query: /* GraphQL */ `
          subscription OnUpdateIssue {
            onUpdateIssue {
              ${selection}
            }
          }
        `,
        authMode: 'userPool',
      })
      .subscribe({
        next: (ev: any) => {
          const row = ev?.data?.onUpdateIssue as Issue | null;
          if (!row) return;
          if (row.id === sessionId) setIssue(row);
        },
        error: () => {},
      });

    return () => {
      try {
        sub?.unsubscribe?.();
      } catch {}
    };
  }, [sessionId]);

  // Auto-redirect based on status
  useEffect(() => {
    if (!matchDetail) return;
    if (!issue?.id) return;

    const status = (issue as any)?.status as string | undefined;
    const hasBest = !!(issue as any)?.bestSolution;
    const dest = routeForStatus(issue.id, status);
    const onAgree = location.pathname.endsWith('/agree');
    const onQuestions = location.pathname.endsWith('/questions');

    if (dest && location.pathname !== dest) {
      if (!onAgree && !onQuestions) {
        if (dest.endsWith('/questions')) {
          const isInitiator = norm(userEmail) === norm((issue as any)?.initiatorEmail);
          const iqI = coerceStrings((issue as any)?.inferredQuestionsInitiator);
          if (isInitiator && iqI.length === 0) {
            // wait on root until initiator questions are generated
          } else {
            navigate(dest, { replace: true });
            return;
          }
        } else {
          navigate(dest, { replace: true });
          return;
        }
      }
    }

    if (!status && hasBest) {
      const fallback = `/session/${encodeURIComponent(issue.id)}/resolve`;
      if (location.pathname !== fallback && !onAgree && !onQuestions) {
        navigate(fallback, { replace: true });
      }
    }
  }, [
    matchDetail,
    location.pathname,
    issue?.id,
    (issue as any)?.status,
    (issue as any)?.bestSolution,
    (issue as any)?.inferredQuestionsInitiator,
    userEmail,
    navigate,
  ]);

  // Auto-navigate INITIATOR to QUESTIONS when questions appear (status accepted/questions)
  useEffect(() => {
    if (!matchDetail) return;
    if (!issue?.id) return;

    const onAgree = location.pathname.endsWith('/agree');
    const onQuestions = location.pathname.endsWith('/questions');
    if (onAgree || onQuestions) return;

    const isInitiator = norm(userEmail) === norm((issue as any)?.initiatorEmail);
    if (!isInitiator) return;

    const st = (issue as any)?.status as string | undefined;
    const iqI = coerceStrings((issue as any)?.inferredQuestionsInitiator);

    const initiatorAnswers = coerceStrings((issue as any)?.initiatorQuestionAnswers);
    const hasAnsweredQuestions =
      initiatorAnswers.length > 0 && initiatorAnswers.length >= iqI.length;

    const shouldGoToQuestions =
      (st === 'accepted' || st === 'questions') &&
      iqI.length > 0 &&
      !hasAnsweredQuestions;

    if (shouldGoToQuestions) {
      const questionsUrl = `/session/${encodeURIComponent(issue.id)}/questions`;
      const isSessionDetail =
        location.pathname === `/session/${encodeURIComponent(issue.id)}`;
      if (isSessionDetail) {
        navigate(questionsUrl, { replace: true });
      }
    }
  }, [
    matchDetail,
    issue?.id,
    (issue as any)?.status,
    (issue as any)?.inferredQuestionsInitiator,
    (issue as any)?.initiatorQuestionAnswers,
    userEmail,
    location.pathname,
    navigate,
  ]);

  // Updated: when collaborator changes status to accepted, push initiator off "waiting" root into /questions
  useEffect(() => {
    if (!matchDetail) return;
    if (!issue?.id) return;

    const isInitiator = norm(userEmail) === norm((issue as any)?.initiatorEmail);
    if (!isInitiator) return;

    const st = (issue as any)?.status as string | undefined;
    const joined = !!(issue as any)?.collaboratorJoined;

    if (st === 'accepted' && joined) {
      const dest = `/session/${encodeURIComponent(issue.id)}/questions`;
      const onQuestions = location.pathname.endsWith('/questions');
      if (location.pathname !== dest && !onQuestions) {
        navigate(dest, { replace: true });
      }
    }
  }, [
    matchDetail,
    issue?.id,
    (issue as any)?.status,
    (issue as any)?.collaboratorJoined,
    userEmail,
    location.pathname,
    navigate,
  ]);

  // Auto-navigate initiator to Agree when proposals ready (unchanged)
  useEffect(() => {
    if (!issue?.id) return;
    const status = (issue as any)?.status as string | undefined;
    const proposals = readCandidates(issue);
    const isInitiator = norm(userEmail) === norm((issue as any)?.initiatorEmail);
    const resolving = status === 'resolving_issue';
    if (isInitiator && resolving && proposals.length >= 4) {
      const agreeUrl = `/session/${encodeURIComponent(issue.id)}/agree`;
      if (location.pathname !== agreeUrl) {
        navigate(agreeUrl, { replace: true });
      }
    }
  }, [issue, userEmail, navigate, location.pathname]);

  /** ---------- LIST VIEW ---------- */
  if (isListView) {
    if (loading) {
      return (
        <div style={shellStyle}>
          <div className="text-center">
            <Spinner animation="border" role="status" />
            <div className="mt-3">Loading your sessions…</div>
          </div>
        </div>
      );
    }
    if (error) {
      return (
        <div style={shellStyle}>
          <Alert variant="danger" className="mb-0">
            {error}
          </Alert>
        </div>
      );
    }

    const data = sessions ?? [];
    let rows: React.ReactNode = null;
    try {
      rows = data.map((s) => {
        const title =
          s.agreedIssue ||
          ((s as any).initiatorIssue?.slice?.(-1)?.[0] ?? 'Untitled session');
        const when = safeDateTime((s as any).createdAt);
        const initiator = fullName(
          (s as any).initiatorFirstName,
          (s as any).initiatorLastName
        );
        const collaborator = fullName(
          (s as any).collaboratorFirstName,
          (s as any).collaboratorLastName
        );
        const st = (s as any).status as string | undefined;
        const isArchiving = archivingIds.has(s.id);

        return (
          <ListGroup.Item
            key={s.id}
            className="d-flex justify-content-between align-items-start"
          >
            <div className="me-3">
              <div className="fw-semibold">{title}</div>
              <div className="text-muted small">
                <span className="me-2">
                  Initiator: <strong>{initiator}</strong>
                </span>
                <span className="me-2">•</span>
                <span>
                  Collaborator: <strong>{collaborator}</strong>
                </span>
                <Badge bg={statusVariant(st)} className="ms-2">
                  {humanizeStatus(st)}
                </Badge>
              </div>
            </div>
            <div className="text-end">
              <div className="small text-muted mb-2">{when}</div>
              <div className="d-flex gap-2 justify-content-end">
                <Link
                  to={`/session/${s.id}`}
                  className="btn"
                  style={{
                    backgroundColor: GOOGLE_GREEN,
                    borderColor: GOOGLE_GREEN,
                    color: '#fff',
                  }}
                  aria-label={`Open session ${s.id}`}
                >
                  Open
                </Link>
                <button
                  type="button"
                  className="btn"
                  style={{
                    backgroundColor: GOOGLE_RED,
                    borderColor: GOOGLE_RED,
                    color: '#fff',
                  }}
                  onClick={() => archiveSession(s.id)}
                  disabled={isArchiving}
                  aria-label={`Delete session ${s.id}`}
                >
                  {isArchiving ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </ListGroup.Item>
        );
      });
    } catch (e: any) {
      rows = (
        <Alert variant="danger">
          Render error: {e?.message ?? String(e)}
        </Alert>
      );
    }

    return (
      <div style={shellStyle}>
        <div className="sessionWrap">
          <Card className="shadow-sm w-100 sessionCardWide">
            <Card.Header as="h4">
              {firstName ? `${possessive(firstName)} Sessions` : 'My Sessions'}
            </Card.Header>
            <Card.Body>
              {data.length === 0 ? (
                <Alert variant="secondary" className="mb-0">
                  You don't have any sessions yet.
                </Alert>
              ) : (
                <ListGroup>{rows}</ListGroup>
              )}
            </Card.Body>
            <Card.Footer className="bg-white">
              <div className="d-flex justify-content-end gap-2">
                <Link
                  to="/"
                  className="btn"
                  style={{
                    backgroundColor: GOOGLE_BLUE,
                    borderColor: GOOGLE_BLUE,
                    color: '#fff',
                  }}
                >
                  Home
                </Link>
                <Link
                  to="/init"
                  className="btn"
                  style={{
                    backgroundColor: GOOGLE_BLUE,
                    borderColor: GOOGLE_BLUE,
                    color: '#fff',
                  }}
                >
                  Start New Session
                </Link>
              </div>
            </Card.Footer>
          </Card>
        </div>
      </div>
    );
  }

  /** ---------- DETAIL VIEW ---------- */
  const initiatorName = useMemo(() => {
    if (!issue) return '';
    const full = `${issue.initiatorFirstName ?? ''} ${
      issue.initiatorLastName ?? ''
    }`.trim();
    return full || (issue.initiatorEmail ?? '');
  }, [issue]);

  const collaboratorFullName = useMemo(() => {
    if (!issue) return '';
    const full = `${issue.collaboratorFirstName ?? ''} ${
      issue.collaboratorLastName ?? ''
    }`.trim();
    return full || (issue.collaboratorEmail ?? '');
  }, [issue]);

  const isInitiator = useMemo(
    () => !!issue && norm(userEmail) === norm(issue?.initiatorEmail),
    [issue, userEmail]
  );

  const youName = isInitiator ? initiatorName : collaboratorFullName;
  const otherName = isInitiator ? collaboratorFullName : initiatorName;

  const initiatorFirst = (issue as any)?.initiatorFirstName || '';
  const collaboratorFirst = (issue as any)?.collaboratorFirstName || '';

  const collaboratorHasSubmitted = useMemo(() => {
    if (!issue) return false;
    const anyText = coerceStrings((issue as any).collaboratorIssue).length > 0;
    const desired =
      coerceStrings((issue as any).desiredOutcomeCollaborator).length > 0;
    const whyLegacy =
      coerceStrings((issue as any).whyOutcomeMattersCollaborator).length > 0;
    const whyCurrent =
      coerceStrings((issue as any).whyOutcomeMattersCollaborator).length > 0;
    const joined = !!(issue as any).collaboratorJoined;
    return anyText || desired || whyLegacy || whyCurrent || joined;
  }, [issue]);

  const collaboratorJoined = !!(issue as any)?.collaboratorJoined;

  const initiatorIssue = useMemo(
    () => coerceStrings((issue as any)?.initiatorIssue).slice(-1)[0] ?? '—',
    [issue]
  );
  const initiatorDesired = useMemo(
    () =>
      coerceStrings((issue as any)?.desiredOutcomeInitiator).slice(-1)[0] ??
      '—',
    [issue]
  );

  const status = (issue as any)?.status ?? 'created';
  const proposals = readCandidates(issue);
  const agreedIssue = (issue as any)?.agreedIssue as string | undefined;

  let topBanner: React.ReactNode = null;

  if (status === 'created') {
    if (isInitiator) {
      topBanner = (
        <div className="sessionNotice" role="status" aria-live="polite">
          <span className="name">{initiatorFirst || 'You'}</span>
          {', waiting for '}
          <span className="name">
            {collaboratorFirst || 'your collaborator'}
          </span>
          {' to join this session.'}
        </div>
      );
    } else if (!collaboratorJoined) {
      topBanner = (
        <div className="sessionNotice" role="status" aria-live="polite">
          You're invited to this session. Please join to begin.
        </div>
      );
    } else if (!collaboratorHasSubmitted) {
      topBanner = (
        <div className="sessionNotice" role="status" aria-live="polite">
          <span className="name">{otherName || 'They'}</span> has joined. We're
          preparing proposed issues.
        </div>
      );
    } else {
      topBanner = (
        <div className="sessionNotice" role="status" aria-live="polite">
          <span className="name">{otherName || 'They'}</span> has started.
          We're preparing proposed issues.
        </div>
      );
    }
  } else if (status === 'accepted') {
    if (isInitiator) {
      topBanner = (
        <div className="sessionNotice" role="status" aria-live="polite">
          <span className="name">
            {collaboratorFirst || 'Your collaborator'}
          </span>{' '}
          accepted this session. Continue to your questions.
        </div>
      );
    } else {
      topBanner = (
        <div className="sessionNotice" role="status" aria-live="polite">
          You accepted this session. Continue to your questions.
        </div>
      );
    }
  }

  return (
    <div style={shellStyle}>
      <ToastContainer position="top-end" className="p-3">
        <Toast
          bg="info"
          onClose={() => setShowInviteToast(false)}
          show={showInviteToast}
          delay={5000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Invitation sent</strong>
          </Toast.Header>
          <Toast.Body className="text-dark">
            We emailed <strong>{otherName || 'your collaborator'}</strong>.
            Waiting for them to join the conflict resolution session.
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="sessionWrap">
        {topBanner}

        <Card className="shadow-sm w-100 sessionCardWide">
          <Card.Header
            as="h4"
            className="d-flex justify-content-between align-items-center"
          >
            <span>Session</span>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={async () => {
                await archiveCurrentSession();
              }}
              disabled={archivingDetail}
              title="Delete this session for both participants"
            >
              {archivingDetail ? 'Deleting…' : 'Delete Session'}
            </Button>
          </Card.Header>

          <Card.Body>
            <div className="d-flex justify-content-between mb-3">
              <div>
                <div className="fw-semibold">You: {youName}</div>
              </div>
              <div className="text-end">
                <div className="fw-semibold">
                  Collaborator: {otherName}
                </div>
              </div>
            </div>

            <div className="mb-2 fw-semibold">Concern</div>
            <Alert variant="light" className="border">
              {initiatorIssue}
            </Alert>

            <div className="mb-2 fw-semibold">Your desired outcomes</div>
            <Alert variant="light" className="border">
              {initiatorDesired}
            </Alert>

            {(status === 'accepted' || status === 'resolving_issue') && (
              <>
                <div className="d-flex align-items-center mb-2 mt-3">
                  <div className="fw-semibold me-2">Proposed issues</div>
                  <Badge bg="info" text="dark">
                    {proposals.length}
                  </Badge>
                </div>

                {proposals.length === 0 ? (
                  <Alert variant="secondary">
                    We're preparing proposed issues based on both perspectives.
                    This will update automatically.
                  </Alert>
                ) : (
                  <>
                    <ListGroup className="mb-3">
                      {proposals.map((c, i) => (
                        <ListGroup.Item key={i}>
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <div className="fw-semibold">
                                Option {i + 1}
                              </div>
                              <div>{c.statement}</div>
                            </div>
                            <div className="text-end small text-muted">
                              {typeof c.score === 'number' ? (
                                <div>Score: {c.score.toFixed(2)}</div>
                              ) : null}
                            </div>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>

                    {isInitiator && proposals.length < 4 && (
                      <Alert variant="info" className="mb-0">
                        Almost there. Once four options are ready, you'll be
                        taken to the next step automatically.
                      </Alert>
                    )}
                  </>
                )}
              </>
            )}

            {status === 'agreed_on_issue' && (
              <Alert variant="success" className="mt-3">
                <div className="fw-semibold mb-1">Agreed issue</div>
                <div>{agreedIssue || '—'}</div>
              </Alert>
            )}
          </Card.Body>

          <Card.Footer className="bg-white">
            <div className="d-flex justify-content-end gap-2">
              <Link
                to="/"
                className="btn"
                style={{
                  backgroundColor: GOOGLE_BLUE,
                  borderColor: GOOGLE_BLUE,
                  color: '#fff',
                }}
              >
                Home
              </Link>
              <Link
                to="/session"
                className="btn"
                style={{
                  backgroundColor: GOOGLE_BLUE,
                  borderColor: GOOGLE_BLUE,
                  color: '#fff',
                }}
              >
                All Sessions
              </Link>
              <Link
                to="/init"
                className="btn"
                style={{
                  backgroundColor: GOOGLE_BLUE,
                  borderColor: GOOGLE_BLUE,
                  color: '#fff',
                }}
              >
                Create New Session
              </Link>
            </div>
          </Card.Footer>
        </Card>
      </div>
    </div>
  );
}
